<template>
  <div class="flex flex-wrap my-10">
    <NuxtLink
      v-for="item in items"
      :key="item.key"
      :to="item.to"
      class="inline-flex items-center py-5 px-10 mr-10 mb-10 border-1 rounded-3xl min-h-20 cursor-pointer"
    >
      <span
        v-if="item.color"
        class="rounded-full size-15 border-transparent m-auto mr-5"
        :style="{ backgroundColor: `#${item.color}` }"
      />

      <span class="text-xs uppercase font-bold leading-none line-clamp-1">
        {{ item.label }}
      </span>
      <ResponsiveSpriteSymbol name="cross" class="size-10 ml-5" />
    </NuxtLink>
  </div>
</template>

<script lang="ts" setup>
import type { ApiSearchFacet, ApiSearchFilterRange } from '@search/types'
import { falsy } from '~/helpers/type'

const props = defineProps<{
  facets: ApiSearchFacet[]
  filters: ApiSearchFilterRange[]
}>()

type Chip = {
  key: string
  label: string
  to: any
  color?: string
}

const route = useRoute()

const items = computed<Chip[]>(() => {
  return Object.entries(route.query)
    .flatMap(([key, value]) => {
      const values = (typeof value === 'string' ? [value] : value) || []

      const facet = props.facets.find((v) => v.key === key)
      if (facet) {
        return values.map((termKey) => {
          const term = facet.terms.find((v) => v.key === termKey)
          if (!term || !term.label) {
            return
          }
          const newValues = values.filter((v) => v !== term.key)
          return {
            key: facet.key + ':' + term.key,
            label: term.label,
            to: {
              query: {
                ...route.query,
                [facet.key]: newValues.length ? newValues : undefined,
              },
            },
            color: term.color,
          }
        })
      }

      const filterKey = key.replace('_min', '').replace('_max', '')
      const filter = props.filters.find((v) => v.field === filterKey)

      if (filter) {
        const minKey = filterKey + '_min'
        const maxKey = filterKey + '_max'
        const minFromQuery = route.query[minKey]
        const maxFromQuery = route.query[maxKey]

        const min = typeof minFromQuery === 'string' ? minFromQuery : filter.min
        const max = typeof maxFromQuery === 'string' ? maxFromQuery : filter.max

        return {
          key: filter.field,
          label: `${filter.label}: ${min} - ${max}`,
          to: {
            query: {
              ...route.query,
              [minKey]: undefined,
              [maxKey]: undefined,
            },
          },
        }
      }

      return null
    })
    .filter(falsy)
})
</script>
